import { useEffect, useMemo, useRef, useState } from 'react';
import {
  App as AntApp,
  Button,
  Card,
  Col,
  Empty,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Skeleton,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { EditOutlined, EyeOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  useCreateDocumentTemplate,
  useDocumentTemplates,
  usePreviewInline,
  useUpdateDocumentTemplate,
  type DocumentTemplate,
  type TemplateLanguage,
  type TemplateType,
} from '../api/document-templates';
import { HtmlEditor, type HtmlEditorHandle } from '../components/html-editor';
import { asApiError } from '@/shared/lib/api';

const TYPES: TemplateType[] = ['contract', 'receipt', 'amendment'];
const LANGS: TemplateLanguage[] = ['ru', 'uz', 'en'];

const STARTER_TEMPLATE = `<h1 style="text-align:center; margin:0 0 4px;">Уй-жой қурилишда улуш билан қатнашиш ҳақида</h1>
<h2 style="text-align:center; margin:0 0 16px;">{{contract.contractNumber}}-сонли шартнома</h2>

<table style="width:100%; border:none; margin-bottom:16px;"><tr>
  <td style="border:none; text-align:left;">Поп шаҳри</td>
  <td style="border:none; text-align:right;">«{{contract.activatedAt}}»</td>
</tr></table>

<p>"POP GARDEN" кўп тармоқли комплексини қуриш ва фойдаланиш лойиҳасини бошқариш шартномаси асосида амал қилувчи кейинчалик "<strong>Қурувчи</strong>" деб номланадиган <strong>"CHORKESAR STROY" MCHJ</strong>, унинг директори <strong>TURSUNBAYEV XALIMJON</strong> вакиллигида, бир томондан ва Ўзбекистон Республикаси фуқароси <strong>{{customer.lastName}} {{customer.firstName}} {{customer.middleName}}</strong>, паспорт серияси <strong>{{customer.passportSeries}} {{customer.passportNumber}}</strong>, <strong>{{customer.passportIssueDate}}</strong>-йилда <strong>{{customer.passportIssuedBy}}</strong> томонидан берилган, кейинчалик "<strong>ИНВЕСТОР</strong>" деб номланадиган бошқа томондан, кейинчалик биргаликда "Томонлар", алоҳида ҳар бири эса "Томон" деб номланиб, ушбу уй-жой қурилишида улуш билан қатнашиш ҳақидаги шартномани (кейинги ўринларда "Шартнома") қуйидаги ҳақида туздилар:</p>

<h3>1. ШАРТНОМА МАЗМУНИ</h3>

<p>1.1. Шартнома буйича Қурувчи Наманган вилоят, Поп туман, МУСТАКИЛЛИК МФЙ, ОЙБЕК кучаси 109-уй жойлашган манзилдаги ер участкасида кўп қаватли уй (кейинчалик POP GARDEN) қурилишини таъминлашни зиммасига олади. Уй-жойни фойдаланишга топшириш ва давлат рўйхатидан ўтказишдан кейин қурувчи шартноманинг 1.4. бандида кўрсатилган объектни шартноманинг 3.2.3. бандига асосан Инвесторга топшириши, Инвестор эса, ўз навбатида шартномада белгиланган тартиб ва муддатларда қурилишда улушли молиялаштиришни амалга оширишни ва шартномани 3.1.3. ва 3.1.4. бандларига мувофиқ объектни ўз мулкига қабул қилиб олишни зиммасига олади.</p>

<p>1.2. Турар жойнинг қурилиш 20.01.2025 й. олди-сотди, реестр №202301475003374 ва қурилиш-монтаж ишларини бошланиши ҳақидаги хабарномага асосан амалга оширилади.</p>

<p>1.3. Турар жой қурилиши тугашини дастлабки муддати тахминан 2026-йил 2-чорак. Турар жой қурилишини тугатилишидан кейин қурувчи инвесторга шартноманинг 1.4. бандида кўрсатилган объектни шартномани 3.1.3-3.1.5 ва 3.2.3-3.2.4. бандларига мувофиқ, шартноманинг 2-сонли иловасига асосан топширишни зиммасига олади. Қурилиш-монтаж ишларини дастлабки тахминий графиги шартноманинг 1-сонли иловасида кўрсатилган. Ушбу мажбуриятни бажарилишига тўсқинлик қиладиган ҳолатлар юзага келганда, шу жумладан қурувчининг иродасига боғлиқ бўлмаганлари билан чегараланмасдан ҳам, шунингдек Давлат қабул қилувчи комиссия далолатномасини имзолаш санаси билан боғлиқ бўлса, қурилишни якунлаш муддати ўзгартирилиши мумкин.</p>

<p>1.4. Турар жойни фойдаланишга топширишга руҳсат олингандан кейин шартнома бўйича қурувчи Инвесторга бериладиган объект бўлиб, шунингдек Инвесторнинг пул маблағларини ҳам жалб қилиб қурилаётган турар-жойни таркибига кирадиган хонадон (квартира) нотурар жой ҳисобланади. Томонлар шунга келишишдики, шартнома бўйича қурувчи Инвесторга қабул қилиш-топшириш далолатномаси бўйича қуйидаги манзилда жойлашган объектни топширишини зиммасига олади: Наманган вилоят, Поп туман, МУСТАКИЛЛИК МФЙ, ОЙБЕК кўчаси 109-уй, турар уй (блокда) <strong>{{apartment.fullCode}}</strong>, майдони <strong>{{apartment.areaSqm}}</strong> кв.м., лойиҳа бўйича (кейинчалик матнда — "Объект").</p>

<p>1.5. Томонлар умумий майдоннинг лойиҳанинг (тахминан) майдон эканлигини ва қурилиш якунида мазкур майдоннинг ўзгариши мумкинлигини келишиб олдилар. Объектнинг якуний майдони ер ресурслари ва кўчмас мулк кадастри ҳизмати томонидан амалга оширилган ўлчовлар асосида аниқланади. Томонлар, қурилиш якунидан кейин ер ресурслари ва кўчмас мулк кадастри ҳизмати томонидан ўлчанган объектнинг майдони якуний ҳисобланишини ва умумий майдонни қайта ўлчашга руҳсат берилмаслигини тасдиқлайдилар. Шартнома бўйича объектни алмаштириш мумкин эмас, шунингдек, қурувчининг олдиндан ёзма розилиги бўлмаган холда шартнома бўйича талаблар ҳуқуқини ўтказиш ёки томонларни алмаштиришга руҳсат берилмайди.</p>

<p>1.6. Агар қурилиш якунлангандан сўнг, объектнинг майдони 2 (икки) квадрат метргача ошса, қурувчи ушбу майдон ошишини тасдиқлайди ва объектнинг умумий майдони ошганлиги сабабли Инвесторга ҳеч қандай даъво қўзғатмайди, шу жумладан объектнинг қийматига қайта ҳисоблаш талаб қилинмайди. Агар объектнинг умумий майдони кадастр ҳужжатларига мувофиқ 2 (икки) квадрат метрдан кўпроқ ўзгарса (ошса), инвестор объект майдонинг ортиши билан боғлиқ қўшимча тўловни, шартнома нархига (шартноманинг 4.1. бандига асосан) мос равишда, бир квадрат метр учун амалга ошириши керак ва тўловни қурувчидан хабарнома олган кундан бошлаб 10 (ўн) банк куни ичида амалга ошириши лозим.</p>

<p>1.7. Қурилиш якунига етганида (хонадоннинг қурилиши тугагандан сўнг) объектнинг майдонининг 2 (икки) квадрат метрга камайиши холатида, Инвестор объектнинг умумий майдонининг камайишига розилик билдиришга ва ушбу камайиш билан боғлиқ равишда қурилиш нархини қайта ҳисоблаш ёки бошқа ҳар қандай даъволарни қўзғатмасликка розилик беради. Агар объектнинг умумий майдони кадастр ҳужжатларига мувофиқ 2 (икки) квадрат метрдан кўпроқ камайган бўлса, қурувчи, Инвесторнинг ёзма аризасига биноан, объект майдонининг камайишига мос равишда, дастлабки шартнома нархидан келиб чиққан холда, қўшимча тўланган маблағларни, яъни кўрсатилган майдон камайишига тенг бўлган қисмини, 10 (ўн) банк куни ичида Инвестор аризасини олганидан сўнг қайтариш мажбуриятини олади.</p>

<h3>2. ОБЪЕКТ ТАВСИФИ, ИШЛАР ТАРКИБИ ВА ҲАЖМИ</h3>
<p>2.1. Қурувчи ушбу шартномага 2-сонли иловада кўрсатилган бажариладиган ишлар объектига нисбатан мавжудлигини таъминлайди.</p>
<p>2.2. Инвестор қурилиш худудини ижтимоий, иқтисодий, экологик холати ҳақида етарли маълумотга эгалигини тасдиқлайди.</p>
<p>2.3. Инвестор юқорида кўрсатилган маълумот ҳажми у учун етарлилигини ва шартнома тузиш учун ва ўзини турар уйни молиялаштиришда улушли иштироки хавфларини баҳолашга қарор қилишга асос бўлганини, ҳамда у шартнома бўйича ўз мажбуриятларини тўғри бажарилиши амалга оширишга имкон берадиган ўзини ҳозирги ва келажакдаги молиявий холатини етарлича баҳолаганини тасдиқлайди.</p>

<h3>3. ТОМОНЛАРНИ ҲУҚУҚ ВА МАЖБУРИЯТЛАРИ</h3>

<p><strong>3.1. Инвесторнинг ҳуқуқ ва мажбуриятлари:</strong></p>
<p>3.1.1. Инвестор қурилишда улуш билан иштирок этиш суммасини тўлиқ ҳажмда шартномада кўрсатилган муддатда киритишга мажбур.</p>
<p>3.1.2. Инвестор қурилишни молиялаштиришни пул маблағлари кўринишида амалга оширишга мажбур.</p>
<p>3.1.3. Инвестор Объектни шартномага қабул қилиш шартномаси бўйича, шартномага мувофиқ Қурувчини объектни топширишга тайёрлиги ҳақидаги хабарномасида кўрсатилган муддатда объектни ўзида қабул қилишга мажбур. Инвестор хабарномада кўрсатилган қабул қилиш муддатини бузганда, қурувчини объектни Инвесторга топшириш бўйича мажбурияти бажарилган ҳисобланади, бу холда қурувчи бир томонлама қабул қилиш-топшириш далолатномаси тузиш ва уни Инвесторга юбориш ҳуқуқига эга ва шу билан биргаликда, қабул қилиш-топшириш далолатномаси тузилгандан кейин, объектга солинадиган солиқлар ва коммунал тўловлар (сув, электр энергияси тўлови ва бошқалар) молиявий мажбуриятлар сифатида баҳоланади ва Инвестор ҳисобидан ундирилади. Кўрсатилган муддат тугашидан кейин объектни ташқи жисмоний таъсирлар оқибатида, шу қаторида қасддан зарар етказилиши таъсирида барбод бўлиши ва ёки бузилишини хавфи, Объект ва умумий турар-жой худудининг ҳаражатлар юки Инвесторга ўтади.</p>
<p>3.1.4. Шартномага 2-сонли иловада кўрсатилган ишлар бажарилмаган холда, Инвестор объектни қабул қилишни рад этиш ҳуқуқига эга.</p>
<p>3.1.5. Объектни қабул қилиш вақтида Инвестор ишлар сифатсиз бажарилганлигини аниқлаганда томонлар сифатсиз ишлар рўйхатини кўрсатиб нуқсонлар далолатномасини тузишади. Бунда Инвестор қабул қилиш-топшириш далолатномасини имзолашни ва объектни қабул қилишни зиммасига олади. Инвестор объектни қабул қилишни рад этганда, ушбу шартномада белгиланган объектни топшириш муддати аниқланган нуқсонларни бартараф этиш ва объектни Инвесторга ҳақиқий топшириш муддатига узайтирилади. Нуқсонларни бартараф этиш тартиби шартномага 3-сонли иловада кўрсатилган.</p>
<p>3.1.6. Қурилиш ишлари тўлиқ якунланганидан сўнг ва объект қабул қилинганидан кейин, объект бўйича барча солиқ ва мажбурий тўловлар сотиб олувчининг ҳисобидан қонунчиликка мувофиқ амалга оширилади. Бироқ, агар сотиб олувчи томонидан шартнома бўйича молиявий мажбуриятлар ўз вақтида тўлиқ бажарилмаган тақдирда, объект тўлиқ эгасига топширилмаслиги мумкин, лекин шу ҳолатда ҳам объект билан боғлиқ солиқ ва бошқа мажбурий тўловлар сотиб олувчининг ҳисобидан тўланиши шарт бўлади.</p>
<p>3.1.7. Инвестор, қабул қилиш ва топшириш далолатномасини имзолашдан бошлаб (шу жумладан, объектни тайёргарлик ишлари учун қабул қилиш ва топшириш далолатномасига асосан қабул қилиш) ўз номига объектга нисбатан мулк ҳуқуқини расмийлаштириш билан боғлиқ барча ҳаражатларни ўз зиммасига олиши, шу жумладан, кўчмас мулк ҳуқуқларини давлат рўйхатидан ўтказиш учун ваколатли органларда, қурувчига объект ва умумий мулк бўйича истеъмол қилинган коммунал ҳизматлар учун тўловларни қайтариш, бошқарув компанияси билан шартнома тузилгунигача ёки уй-жой мулкдорлари жамиятини ташкил этилгунигача умумий мулкни сақлаш бўйича эксплуатацион ҳаражатлар, ҳамда ушбу шартнома ва объектни сақлаш билан боғлиқ бошқа ҳаражатларни ўз зиммасига олиши лозим. Инвестор, юқорида кўрсатилган ҳаражатларни қурувчи томонидан талабнома олинган кундан бошлаб 10 (ўн) иш куни давомида қоплаб беришга мажбур.</p>
<p>3.1.8. Инвестор хонадон/яшаш уйларни нотурар жойлари ва уй атрофи худудидан фойдаланишини техник, ёнғинга қарши ва санитар (реконструкция қилмаслик, кўтарувчи деворларни бузмаслик, тешмаслик, очмаслик ва ҳ.к.) қоидаларига ва ЎзР қонунчилигига асосан риоя қилишга мажбур, шунингдек турар уйни юзасини ва умумий фойдаланиш мулкини кўринишини турли жиҳозлар, қурилмалар, тўсиқлар ва мосламалар (антенналар, панжаралар, қуритгичлар, кондиционерларнинг ташқи блоклари ва ҳ.к.) ўрнатишда ўзгартирмаслик, бунақа ишларни амалга оширишга Қурувчининг ёзма розилиги олинган холлар бундан мустасно. Акс холда ўзгартириш, қайта режалаштириш туридан қатъий назар Инвесторнинг кўрсатилган ҳаракатлари қурувчига зарар етказадиган ҳаракат ҳисобланиб, шу сабабли қурувчи ушбу банд бузилишини ҳар бир холатида шартномани 4.1. бандида кўрсатилган улуш билан қатнашиш суммасини 5% миқдорида жарима қўллаш ҳуқуқига эга бўлади. Бу холда Инвестор қилинган ўзгаришларни аввалги холатига келтириши лозим ва бу билан боғлиқ ҳамма салбий оқибатларини мустақил қоплайди.</p>
<p>3.1.9. Инвесторни "Бозор тамойилларига асосланган ипотека кредитлари орқали аҳолини уй-жой билан таъминлашга оид қўшимча чора-тадбирлар тўғрисида" 09.12.2021 йилдаги 33-сонли Ўзбекистон Республикаси Президентининг Фармони, "Ипотека тўғрисида" Қонунини 63 м.га асосан қурилиши тугалланмаган объект — Наманган вилоят, Поп туман, МУСТАКИЛЛИК МФЙ, ОЙБЕК кучаси 109-уй манзилда жойлашган кўп квартирали уйни то ушбу қурилиши тугалланмаган объектни фойдаланишга топшириш ва унга кадастр ҳужжатларини расмийлаштиришгача гаровга берилишигача эътирози йўқ.</p>
<p>3.1.10. Инвестор ўзини реквизитларини (паспорт маълумотларини, манзилини, ҳисоб рақамини, телефонларини ва ҳ.к.) ўзгариши ҳақида қурувчини, шунингдек уй-жой мулкдорлари ширкатини ёки Турар уйни умумий мулкини бошқарадиган бошқарувчи компанияни хабардор қилишга ва янги маълумотларни юз берган ўзгаришлардан кейин 7 (етти) календар куни ичида маълум қилиш, шунингдек ҳуқуқ ва мажбуриятларни бошқа учинчи шахсларга берганда ушбу шахслар ҳақида керакли маълумотларни тақдим этишга мажбур.</p>
<p>3.1.11. Инвестор, объектни қабул қилиш ва топшириш пайтида, умумий мулкни қабул қилиш ҳуқуқини янги ташкил этилган уй-жой жамиятига ёки Қурувчининг танлаган бошқарув компаниясига топширишга рози бўлади, шунингдек, умумий мулкни сақлаш бўйича шартнома тузиш ва умумий мулкни сақлаш билан боғлиқ ҳаражатларни қоплаш учун мажбурий тўловлар ва ҳаражатларни ўз вақтида амалга ошириш мажбуриятини ўз зиммасига олади. Уй-жой эгалари умумий йиғилиши ўтказилгунига қадар, Инвестор умумий мулкни сақлаш бўйича, қурувчи томонидан танланган бошқарув компаниясининг ҳизматларини тўлаш орқали ҳаражатларни ўз вақтида амалга оширишга мажбур.</p>
<p>3.1.12. Объектга эгалик ҳуқуқини берувчи ҳужжатларни (Ўзбекистон Республикаси қонунчилигига мувофиқ тегишли кадастр ва бошқа ҳужжатлар) олмагунча Инвестор қурувчи билан келишмасдан объектни қайта ўзгартириш ва таъмирлаш ҳуқуқига эга эмас. Қайта ўзгартириш ва таъмирлаш холатида Инвестор ушбу шартномани 3.1.8. бандини бузмасликни зиммасига олади.</p>

<p><strong>3.2. Қурувчининг ҳуқуқ ва мажбуриятлари:</strong></p>
<p>3.2.1. Қурувчи турар уйни фойдаланишга топширишни шаҳарсозлик меъёрлари ва қоидаларига мувофиқ амалга оширишга мажбурдир.</p>
<p>3.2.2. Қурувчи Инвестор қурилиш тугаши ҳақидаги хабарномани олган вақтдан бошлаб, Инвестор улуш билан қатнашиш суммасини киритиш бўйича ўз мажбуриятини тўлиқ бажарган тақдирда, шу жумладан жарима (неустойка)ни тўлаш бўйича (тўловни кечиктирилган бўлса), Объектни 60 (олтмиш) календар куни давомида Инвесторга топширишга, шунингдек Инвесторга барча керакли ҳужжатларни Инвесторга мулк ҳуқуқини расмийлаштириш учун тақдим этишга мажбур. Агар объектни қурилиши тугатилган вақтига келиб Инвестор шартнома бўйича тўлиқ тўловни амалга оширмаган бўлса (сабабидан қатъий назар), объектни топшириш муддати 60 (олтмиш) кун тўлиқ тўлаганидан кейин бошланади.</p>
<p>3.2.3. Қурувчи Инвесторни объект қурилишини тугаши ва топширишга тайёрлиги ҳақида турар уйнинг қурилиши тугаган ва давлат қабул қилиш комиссиясининг турар уйни фойдаланишга қабул қилиш ҳақидаги далолатномасини расмийлаштирилган санадан бошлаб 60 (олтмиш) календар кун ичида ҳар қандай йўл билан (телефон, СМС ва ҳ.к.) хабардор қилади. Агар Инвесторга объект қурилишини тугаши ва топширишга тайёрлиги ҳақида кўрсатилган муддатда хабарнома жўнатилмаса, Объект инвесторга турар уйнинг қурилиши тугаган ва давлат қабул қилиш комиссиясининг турар уйни фойдаланишга қабул қилиш ҳақидаги далолатномаси расмийлаштирилган санадан бошлаб 90 (тўқсон) календар кун ичида топширилиши лозим.</p>
<p>3.2.4. Қурувчи бир томонлама тартибда қурилишни тугатиш муддатини ўзгартириш ҳуқуқига эга. Агар бу объектив тарзда шаҳарсозлик норма ва қоидаларини қўшимча талабларини бажариш билан боғлиқ бўлса ва ёки ушбу мажбуриятни бажаришга тўсқинлик қиладиган ва Қурувчини иродасига боғлиқ бўлмаган холатлар пайдо бўлганда, ва шунингдек Давлат қабул қилувчи комиссияси далолатномасини имзолаш санасига боғлиқ бўлса. Бу холда қурувчи Инвесторни шартномада кўрсатилган манзилига қўшимча келишув имзолаш ҳақида хабарнома йўллайди. Агар Инвестор 10 (ўн) кун давомида муддатларни ўзгариши ҳақида шартномага қўшимча келишувни имзоламаса ва имзолашни асосланган рад жавобини тақдим этмаса, қўшимча келишув қабул қилинган ва имзоланган ҳисобланади.</p>

<h3>4. УЛУШ БИЛАН ҚАТНАШИШ СУММАСИ ВА МОЛИЯЛАШТИРИШ ТАРТИБИ</h3>

<p>4.1. Инвесторни қурилишда улуш билан қатнашиш суммаси ва объектни қиймати (шартнома қиймати) <strong>{{contract.costPriceFormatted}}</strong> сўм ҚҚС билан.</p>

<p>4.2. Инвесторни қурилишда улуш билан қатнашиш суммасини ҳисоблаш учун шартномани 1.4. бандига мувофиқ умумий майдон қўлланади ва <strong>{{contract.costPricePerSqmFormatted}}</strong> сўм ҚҚС билан бўлган 1 кв.м қиймати олинади. Шартнома бўйича тўлов Инвестор нақд пул маблағларини жалб қилиб қурувчининг ҳисоб рақамига ўтказиш йўли билан қуйидаги тартибда амалга оширилади.</p>

<p>4.3. Тўловни биринчи босқичи <strong>{{contract.downPaymentFormatted}}</strong> сўм миқдорида Инвестор ушбу шартномани имзолаган пайтидан 3 (уч) иш кунидан кечиктирмасдан амалга оширилади.</p>

<p>4.4. Қолган тўловлар <strong>{{contract.remainingFormatted}}</strong> сўм миқдорида бўлиб, шартноманинг 1-сонли иловасига асосан (<strong>{{contract.installmentCount}}</strong> та тўлов) ойма-ой бўлиб тўлаб борилади. Биринчи навбатдаги тўлов санаси: <strong>{{contract.firstDueDate}}</strong>. Тўлов қурувчининг ҳисоб рақамига "CHORKESAR STROY" MCHJ, ҳисоб рақами: <strong>20208000607107993001</strong>, МФО: <strong>00433</strong>, "MIKROKREDITBANK" ATB BOSH OFISI, СТИР: <strong>311454694</strong> орқали амалга оширилади. Агар тўлов 3 (уч) ой давомида амалга оширилмаса, бу Қурувчи томонидан шартномани бир томонлама равишда кўриб чиқиш ва бекор қилиш учун асос бўлиши мумкин.</p>

<p>4.5. Инвестор шартнома шартларини бузса, мажбуриятларини бажармаса ёки нотўғри бажарса, қурувчи Инвесторга хабарнома юбориб ҳар қандай пайтда шартномани бир томонлама бекор қилиш ва бу холда инвестор тўлаган дастлабки ва шартнома бўйича суммадан барча қилинган ҳаражатларни, шу жумладан кафиллик шартномаси бўйича, ушламаслик ҳуқуқига эга.</p>

<p>4.6. Инвестор тўлов ҳужжатининг деталларида қуйидагини: "Қурилиш молиялаштириш бўйича тўлов, уй-жой қурилишига доир улуш шартномасига мувофиқ {{contract.contractNumber}}-сонли шартнома, {{contract.activatedAt}} санаси" ни кўрсатиши керак.</p>

<p>4.7. Инвестор томонидан улушдорлик иштирок суммасини пул маблағлари кўринишида киритганини тасдиқловчи ҳужжат банкнинг тўловни қабул қилганлиги тўғрисидаги белгига эга бўлган тўлов топшириғи (квитанция) ҳисобланади.</p>

<p>4.8. Инвесторнинг улушдорлик иштирок суммасини киритиш мажбуриятини бажарган сана сифатида, банкнинг қурувчини ҳисоб рақамига пул маблағлари киритилган сана ҳисобланади ва бу ҳақидаги маълумот қурувчига ҳизмат кўрсатувчи банкнинг ҳисоб вароғидан аниқланади.</p>

<p>4.9. Инвестор қурувчини шартномада кўрсатилган мақсадлар учун улушдорлик иштирок суммасига эгалик қилиш, фойдаланиш ва ундан фойдаланиш бўйича чекланмаган ваколатлар билан таъминлайди.</p>

<p>4.10. Шартноманинг 4.1. бандида кўрсатилган улушдорлик иштирок суммаси ўзгармас бўлиб, фақат шартноманинг 1.6. ва 1.7. бандларида кўрсатилган холатлардан бундан истисно. Агар ҚҚС ставкасининг ўзгариши (камайиши ёки ошиши) содир бўлса, шартноманинг умумий қиймати ўзгармасдан қолади.</p>

<p>4.11. Тўлов қурувчини 4.1. бандда кўрсатилган ҳисоб рақамига нақд пул ва нақдсиз ёки тўлов тизимлари орқали (Click, Payme ва бошқа банк иловалари) амалга оширилиши мумкин. Бу холда тўлов мақсадини 4.6. бандида кўрсатилгандек кўрсатиш керак бўлади.</p>

<h3>5. АЛОҲИДА ШАРТЛАР ВА СИФАТ КАФОЛАТЛАРИ</h3>
<p>5.1. Инвесторни объектга мулк ҳуқуқи Ўзбекистон Республикаси қонунчилигига мувофиқ мулк ҳуқуқини давлат рўйхатига олишдан кейин пайдо бўлади.</p>
<p>5.2. Инвестор ушбу шартномада кўзланган муддатлар ва тартибда, қурувчи иштирокида объектни кўздан кечириб қабул қилиши, иш натижаларини ёмонлаштирадиган шартномадан четга чиқишлар, ёки ишдаги бошқа камчиликлар аниқланганда бу ҳақида дархол қурувчига мурожаат этишга мажбур. Объектни қабул қилишда унда камчиликларни аниқлаган Инвестор, агар ушбу камчиликлар далолатномада ёки қабул қилишни тасдиқловчи бошқа ҳужжатларда кўрсатилган бўлсагина, уларни пеш қилиш ҳуқуқига эга. Объектни текширмасдан қабул қилган Инвестор, қабул қилишнинг одатдаги усулида аниқланиши мумкин бўлган камчиликларни (яққол камчиликлар) пеш қилиш ҳуқуқидан махрум бўлади.</p>
<p>5.3. Томонлар шундан келиб чиқишадики, объект сифатини, уни лойиҳага, шаҳарсозлик меъёр ва қоидаларига мувофиқлигини кафолати бўлиб ўрнатилган тартибда рўйхатга олинган Давлат қабул комиссиясининг турар уйни тугалланган қурилишини фойдаланишга қабул қилиниши ҳақидаги далолатномаси ҳисобланади. Қурилиш объектини кафолатли фойдаланиш муддати Турар уйни қурилишини тугалланиш вақтидан бошлаб 24 (йигирма тўрт) календар ойдан иборат ва фақат ушбу шартноманинг 2-сонли иловасида кўрсатилган бажарилган ишларни қамраб олади. Бунда кафолат қуйидаги холларда амал қилмайди:</p>
<ul>
  <li>агар улар объектни ёки унинг қисмларини меъёрий эскириши таъсирида ёки Инвестор нотўғри фойдаланганда;</li>
  <li>Турар жой биносини умумий мулкини фойдаланишга уй-жой мулкдорлари ширкати ёки бошқарувчи компания билан шартнома тузилмаганда;</li>
  <li>Қурувчи аввал бажарган ҳар қандай ишларни Инвестор томонидан бажарганда;</li>
  <li>Иситиш, сантехника, электрик ва бошқа жиҳозларни уланиши ва фойдаланишида техник ва бошқа қоидалар, меъёрлар талабларини бузилишида;</li>
  <li>Иситиш, сув таъминоти ва сув оқова, вентилляция ва электр тармоғи тизимларида учинчи шахслар, яшовчилар, офат ва ҳ.к. сабаб бўлган фавқулодда холатларда;</li>
  <li>Яшовчилар ва учинчи шахслар ҳаракатлари билан боғлиқ бузилишлар ва бошқа нуқсонлар;</li>
  <li>Учинчи шахслар, яшовчилар, офат ва ҳ.к. томонидан таъмирни бузилиши;</li>
  <li>Қурувчи томонидан фасад қисмларини келишилмаган жойларида жиҳоз ўрнатиш йўли билан бузиш;</li>
  <li>Яшовчилар ва учинчи шахслар ҳаракатлари билан боғлиқ том конструкцияларини бузилишлари;</li>
  <li>Хонадонлар таъмирини яшовчилар фойдаланиши даврида бузилиши.</li>
</ul>
<p>5.4. Инвесторни эътирозларини қурувчи эътирозни ўрнатилган тартибда олиш санасидан бошлаб 2 (икки) ой давомида кўриб чиқилади.</p>

<h3>6. ТОМОНЛАР ЖАВОБГАРЛИГИ</h3>
<p>6.1. Инвестор тўлов биринчи босқичини муддат ва шартларини ва ушбу шартноманинг 1-сонли иловасида кўрсатилган молиялаштириш суммасини бузган тақдирда, ушбу шартнома қурувчи томонидан шартномани бузиш учун асос ҳисобланади. Бунда, Инвестор ушбу шартномани 4.1. бандида ўрнатилган якуний тўловни муддат ва шартларини бузган холатда шартнома бир томонлама, ҳар қандай вақтда бу ҳақида Инвесторни алоқани ҳар қандай мавжуд усуллари билан хабардор қилиниб, Қурувчи томонидан бекор қилиниши мумкин. Шартнома ушбу бандда кўрсатилган асосларда бекор қилинган холатда, Инвестор шартноманинг 4.1. бандида ўрнатилган улуш билан қатнашиш сифатида киритилган маблағни қурувчи шартнома бекор қилингандан кейин 90 (тўқсон) банк куни ичида Инвесторнинг ушбу шартнома реквизитларида кўрсатилган ҳисоб рақамига қайтаради. Агар Инвестор ихтиёрий равишда шартномани бекор қилишни сўраса, бугунги кунда амалга оширилган тўловлар Инвесторга 180 (бир юз саксон) иш куни ичида қайтарилади.</p>
<p>6.2. Шартнома бўйича ҳар қандай маълумотга тегишли бўлган масалаларда томонлар сир сақлаш мажбуриятини олишади, шартнома ва уни алоҳида бандларини мазмунини учинчи шахсларга ошкор этмайди. Акс холда, айбдор томон бошқа томонга етказилган зарарни қоплаш мажбуриятини олади.</p>
<p>6.3. Инвестор томонидан ҳар қандай ОАВ орқали, ижтимоий тармоқлар, интернет, оммавий чиқишлар ёки оғзаки равишда учинчи шахсларга қурувчининг ишбилармонлик обрўсига қарши йўналтирилган, ёки ёлғон, тасдиқланмаган маълумотларни тарқатилганда, Инвестор бундай ҳаракатлар билан етказилган моддий ва маънавий зарарни қоплашга мажбур, шунингдек Қурувчига шартнома суммасини 50% миқдоридаги жарима тўлаши шарт. Кўрсатилган холатда қурувчи шартномани бир томонлама бекор қилиш ҳуқуқига эга.</p>
<p>6.4. Ушбу шартномада кўрсатилганлардан ташқари томонларга нисбатан жавобгарликни бошқа турлари қўлланилмайди.</p>

<h3>7. ФОРС-МАЖОР</h3>
<p>7.1. Томонларни шартнома бўйича мажбуриятларини бажаришига таъсир кўрсатадиган енгиб бўлмас вазиятлар, яъни: сув тошқинлари, ёнғинлар, зилзилалар, Объектнинг қурилиши ман қилинадиган иқлим шароитлари, эпидемиялар, ҳарбий жанжаллар, террорчилик ҳаракатлари, фуқаролик тартибсизликлари, иш ташлашлар, давлат ҳукумати органлари томонидан чиқарилган ёки қабул қилинган кўрсатмалар, буйруқлар, қарорлар, ҳаракатлар ва бошқа чекловлар юз берган холатда ушбу мажбуриятларни бажариш муддатлари бу вазиятларни амал қилиш вақтига мутаносиб равишда сурилади, агар улар шартномани ё унинг форс-мажор вазиятлар юз берганидан кейин бажарилиши лозим бўлган қисмини муддатларига катта таъсир этса.</p>
<p>7.2. Форс-мажор вазиятлар кетма-кет ёки ҳаммаси бўлиб 3 (уч) ойдан кўпроқ давом этса, Инвесторга улуш билан қатнашиш суммаси 90 (тўқсон) календар кун давомида қайтарилиб, шартнома ҳар қандай томон ташаббуси билан бекор қилиниши мумкин.</p>

<h3>8. ШАРТНОМАНИ ЎЗГАРТИРИШ ВА БЕКОР ҚИЛИШ</h3>
<p>8.1. Шартномани муддатдан олдин қуйидаги холларда бекор қилиш мумкин:</p>
<p>8.1.1. Томонларни ўзаро розилиги билан;</p>
<p>8.1.2. Қурувчини ташаббуси билан, шартномани 4.1. бандига асосан бир томонлама;</p>
<p>8.1.3. Форс-мажор вазиятлар туфайли шартномани 7.2. бандига асосан.</p>
<p>8.2. Томонлардан бири шартномада кўзда тутилган асослар бўйича, шартномани бажаришдан бир томонлама тартибда бош тортган холатда, бошқа томонга шартномани бажаришдан бош тортиши ҳақидаги ёзма хабарни ушбу шартномада кўрсатилган бошқа томонни манзилига юборилган кундан бошлаб, у бекор қилинган ҳисобланади.</p>
<p>8.3. Шартномада кўзда тутилмаган холатларда томонлар Ўзбекистон Республикасининг амалдаги қонунчилигига асосланади.</p>
<p>8.4. Шартномани иловалари унинг ажралмас қисмидир.</p>
<p>8.5. Реквизитлари ўзгарганда томонлар бир-бирини 7 (етти) кун ичида ўзгаришлар ҳақида ёзма равишда хабардор қилиши ва янги реквизитлари маълум этишга мажбурлар.</p>

<h3>9. ТОМОНЛАРНИНГ ИШОНТИРИШ ВА ТАСДИҚЛАШЛАРИ</h3>
<p>9.1. Томонлар тасдиқлашадики, ушбу шартноманинг ҳамма шартларини томонлар келишишган ва уларни ҳуқуқ ва манфаатларини камситмайди. Ушбу шартнома ихтиёрий ва онгли равишда имзоланган, ҳуқуқий оқибатлари маълум ва тушунарли, шартнома матни билан танишганлар, маъноси тушунарли, ушбу шартнома тузилган тилига эътирозлар йўқ, бу ҳақда томонларни шартномани имзолашгани далолат беради.</p>
<p>9.2. Инвестор, ушбу шартнома бўйича тўловларни ўз вақтида тўламаган тақдирда, тўловларни амалга ошириш учун, унга Банк-ҳамкорни кредит маблағларини жалб этишни шарт ва оқибатлари тушунтирилганлигини тасдиқлайди.</p>
<p>9.3. Инвестор шартноманинг амал қилиш муддатида, шартноманинг бекор қилиниши холатида, бекор қилиш сабабидан қатъий назар, Инвестор томонидан шартнома шартларининг бузилиши сабабли қурувчи томонидан шартномани бекор қилинишида, қурувчи Инвестордан олинган маблағларни қайтараётганда, қурувчи томонидан кетган ҳаражатларни (кафиллик ва бошқа ҳаражатларни) ушлаб қолиш ҳуқуқига эга эканини тасдиқлайди.</p>

<h3>10. НИЗОЛАРНИ ХАЛ ҚИЛИШ</h3>
<p>10.1. Шартнома бўйича ё у билан боғлиқ мажбуриятларни ижро этишида томонлар ўртасида келиб чиқиши мумкин бўлган низо ва келишмовчиликлар томонлар ўртасида музокара йўли билан хал этилади.</p>
<p>10.2. Ҳар қандай бахс, тушунмовчилик, даъво, шу шартномадан ёки унинг бузилиши, бекор қилиниши ёки нуфузсиз деб топилиши билан боғлиқ, шунингдек, тарафлар томонидан бошқа юридик мажбуриятли ҳаракатларни амалга ошириш тартиби ва шартлари бўйича тушунмовчиликлар хакамлик судида Ўзбекистон халқаро арбитраж ва хакамлик судларини ривожлантириш жамоат фондининг шартлари асосида, унинг Низоми, Регламенти ва хакамлик ишлари бўйича йиғимлар, ҳаражатлар ва сарфлар Низомига мувофиқ хал этилади. Хакамлик суд раисининг ёки унинг тайинланган судьясини ягона қарор чиқариши асосида, хакамлик суд қарорлари якуни ва тарафлар учун мажбурий ҳисобланади. Суд иш юритиш тили ўзбек тилида бўлади.</p>

<h3>11. ЯКУНИЙ ҚОИДАЛАР</h3>
<p>11.1. Ушбу билан Инвестор шахсий маълумотларини ушбу шартномани бажариш билан боғлиқ мақсадларда "Шахсга доир маълумотлар тўғрисида" ЎзР Қонуни талабларига мувофиқ ишлов берилишига қурувчига розилик беради.</p>
<p>11.2. Қурувчи Инвесторга жўнатган ҳар қандай корреспонденция, жўнатилган санасидан Инвестор томонидан олинган ҳисобланади.</p>
<p>11.3. Шартнома имзоланган лаҳзадан бошлаб, аввалги барча келишувлар, музокаралар, хат алмашишлар, ахборот ва реклама мавзусидаги материаллар, ҳужжатлар ва шартномада ёзилган масалаларга оид бошқа материаллар кучини йўқотади.</p>
<p>11.4. Ушбу Шартнома пудрат шартномаси эмас ва Ўзбекистон Республикаси Президентини № ПҚ-4732 "Кўп квартирали уйларни улуш киритиш асосида қуриш жараёнини тартибга солиш чора-тадбирлари ҳақида"ги 27.05.2020 йилдаги Қарори билан тартибга солинади.</p>
<p>11.5. Томонлар тасдиқлашадики, ушбу шартномани ҳамма шартларини икки томон келишган ва уларнинг ҳуқуқ ва манфаатларини камситмайди. Ушбу шартномани улар ихтиёрий ва онгли равишда имзолаган, Шартномани ҳуқуқий оқибатлари маълум ва тушунарли, шартнома матни билан танишганлар, маъноси аниқ, матни тушунарли, томонлар уни имзолагани бу ҳақда далолат беради.</p>
<p>11.6. Шартнома ўзбек тилида 2 (икки) нусҳада тузилган бўлиб, ҳар икки нусҳа ҳам бир хил юридик кучга эга ва ҳар бир тарафга бир нусҳадан топширилади.</p>

<h3 style="margin-top:24px;">ТОМОНЛАРНИ МАНЗИЛЛАРИ, БАНК РЕКВИЗИТЛАРИ ВА ИМЗОЛАРИ</h3>

<table style="width:100%; border-collapse:collapse; margin-top:12px;">
  <tr>
    <td style="width:50%; vertical-align:top; padding:8px; border:1px solid #999;">
      <p><strong>ҚУРУВЧИ</strong></p>
      <p><strong>"CHORKESAR STROY" MCHJ</strong></p>
      <p>Манзил: Наманган ш., Поп тумани, MUSTAQILLIK MFY, OYBEK ko\`chasi 109</p>
      <p>х/р: 20208000607107993001</p>
      <p>МФО: 00433</p>
      <p>Банк: "MIKROKREDITBANK" ATB BOSH OFIS</p>
      <p>СТИР: 311454694</p>
      <p>Тел: +998974310886</p>
      <p style="margin-top:32px;">Директор: ____________________ X.X. Tursunbayev</p>
    </td>
    <td style="width:50%; vertical-align:top; padding:8px; border:1px solid #999;">
      <p><strong>ИНВЕСТОР</strong></p>
      <p><strong>{{customer.lastName}} {{customer.firstName}} {{customer.middleName}}</strong></p>
      <p>Манзил: {{customer.registrationAddress}}</p>
      <p>Паспорт серияси: {{customer.passportSeries}} {{customer.passportNumber}}</p>
      <p>Берилган сана: {{customer.passportIssueDate}}</p>
      <p>Кимдан берилган: {{customer.passportIssuedBy}}</p>
      <p>Туғилган вақти: {{customer.dateOfBirth}}</p>
      {{#if customer.pinfl}}<p>ЖШШИР: {{customer.pinfl}}</p>{{/if}}
      <p>Тел: {{customer.phone}}</p>
      <p style="margin-top:32px;">Инвестор: ____________________ {{customer.lastName}} {{customer.firstName}}</p>
    </td>
  </tr>
</table>

<h4 style="margin-top:24px;">1-сонли илова. Тўлов графиги</h4>
<table border="1" cellspacing="0" cellpadding="6" style="border-collapse: collapse; width:100%;">
  <thead>
    <tr>
      <th>№</th>
      <th>Тўлов санаси</th>
      <th>Сумма (сўм)</th>
      <th>Тури</th>
    </tr>
  </thead>
  <tbody>
    {{#each schedules}}
    <tr>
      <td style="text-align:center;">{{this.installmentNumber}}</td>
      <td>{{this.dueDate}}</td>
      <td style="text-align:right;">{{this.amountDueFormatted}}</td>
      <td>{{#if this.isDownPayment}}Бошланғич{{/if}}</td>
    </tr>
    {{/each}}
  </tbody>
</table>`;

const RECEIPT_STARTER_TEMPLATE = `<style>
  .kv-info { width:100%; margin-bottom:12px; }
  .kv-info td { border:none; padding:4px 0; }
  .kv-total td { border:none; border-top:1px solid #999; padding:10px 0; font-size:14pt; }
</style>
<h2 style="text-align:center; margin:0 0 4px;">ТЎЛОВ КВИТАНЦИЯСИ</h2>
<p style="text-align:center; margin:0 0 16px;">№ {{payment.idShort}} · {{payment.paymentDate}}</p>

<table class="kv-info">
  <tr><td>Мижоз:</td><td style="text-align:right;"><strong>{{customer.lastName}} {{customer.firstName}} {{customer.middleName}}</strong></td></tr>
  <tr><td>Телефон:</td><td style="text-align:right;">{{customer.phone}}</td></tr>
  <tr><td>Шартнома:</td><td style="text-align:right;">{{contract.contractNumber}}</td></tr>
  <tr><td>Тўлов санаси:</td><td style="text-align:right;">{{payment.paymentDate}}</td></tr>
  <tr><td>Тўлов усули:</td><td style="text-align:right;">{{payment.method}}</td></tr>
  <tr><td>Изоҳ:</td><td style="text-align:right;">{{payment.reference}}</td></tr>
  <tr class="kv-total"><td>Жами тўланди:</td><td style="text-align:right;"><strong>{{payment.amountFormatted}} сўм</strong></td></tr>
</table>

<h4>Қопланган тўлов(лар)</h4>
<table border="1" cellspacing="0" cellpadding="6" style="border-collapse: collapse; width:100%;">
  <thead>
    <tr>
      <th>№</th>
      <th>Тўлов муддати</th>
      <th>Сумма (сўм)</th>
    </tr>
  </thead>
  <tbody>
    {{#each allocations}}
    <tr>
      <td style="text-align:center;">{{this.installmentNumber}}</td>
      <td>{{this.dueDate}}</td>
      <td style="text-align:right;">{{this.amountFormatted}}</td>
    </tr>
    {{/each}}
  </tbody>
</table>

<p style="margin-top:32px;">Қабул қилди: ____________________</p>`;

/** Starter body per template type — offered when creating a brand-new template. */
const STARTERS: Record<TemplateType, string> = {
  contract: STARTER_TEMPLATE,
  receipt: RECEIPT_STARTER_TEMPLATE,
  amendment: STARTER_TEMPLATE,
};

export function DocumentTemplatesPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useDocumentTemplates();
  const [editing, setEditing] = useState<DocumentTemplate | null>(null);
  const [open, setOpen] = useState(false);

  function openCreate() {
    setEditing(null);
    setOpen(true);
  }
  function openEdit(tpl: DocumentTemplate) {
    setEditing(tpl);
    setOpen(true);
  }

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          {t('docTemplates.title')}
        </Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          {t('docTemplates.new')}
        </Button>
      </div>

      <Card bodyStyle={{ padding: 0 }}>
        {isLoading ? (
          <Skeleton active style={{ padding: 24 }} />
        ) : (data ?? []).length === 0 ? (
          <Empty style={{ padding: 32 }} description={t('docTemplates.empty')} />
        ) : (
          <Table<DocumentTemplate>
            rowKey="id"
            dataSource={data ?? []}
            pagination={false}
            size="small"
            columns={[
              { title: t('docTemplates.code'), dataIndex: 'code', width: 200 },
              { title: t('docTemplates.name'), dataIndex: 'name' },
              {
                title: t('docTemplates.type'),
                dataIndex: 'type',
                width: 120,
                render: (v: TemplateType) => <Tag>{t(`docTemplates.types.${v}`)}</Tag>,
              },
              {
                title: t('docTemplates.language'),
                dataIndex: 'language',
                width: 100,
                render: (v: string) => v.toUpperCase(),
              },
              { title: 'v', dataIndex: 'version', width: 60 },
              {
                title: t('docTemplates.active'),
                dataIndex: 'active',
                width: 80,
                render: (v: boolean) => <Tag color={v ? 'green' : 'default'}>{v ? '●' : '—'}</Tag>,
              },
              {
                title: '',
                width: 60,
                render: (_, r) => (
                  <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
                ),
              },
            ]}
          />
        )}
      </Card>

      <TemplateEditor
        open={open}
        template={editing}
        starters={STARTERS}
        onClose={() => setOpen(false)}
      />
    </Space>
  );
}

type FormValues = {
  code: string;
  name: string;
  type: TemplateType;
  language: TemplateLanguage;
  bodyHtml: string;
  active: boolean;
};

function TemplateEditor({
  open,
  template,
  starters,
  onClose,
}: {
  open: boolean;
  template: DocumentTemplate | null;
  starters: Record<TemplateType, string>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const [form] = Form.useForm<FormValues>();
  const create = useCreateDocumentTemplate();
  const update = useUpdateDocumentTemplate();
  const preview = usePreviewInline();
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [placeholders, setPlaceholders] = useState<string[]>([]);
  const editorRef = useRef<HtmlEditorHandle>(null);
  const body = Form.useWatch('bodyHtml', form);
  const type = (Form.useWatch('type', form) ?? 'contract') as TemplateType;

  useEffect(() => {
    if (!open) return;
    if (template) {
      form.setFieldsValue({
        code: template.code,
        name: template.name,
        type: template.type,
        language: template.language,
        bodyHtml: template.bodyHtml,
        active: template.active,
      });
    } else {
      form.setFieldsValue({
        code: 'contract-standard',
        name: 'Стандартный договор',
        type: 'contract',
        language: 'ru',
        bodyHtml: starters.contract,
        active: true,
      });
    }
    setPreviewHtml('');
    setPlaceholders([]);
  }, [open, template, form, starters]);

  // When creating a NEW template, swap the starter body to match the chosen
  // type — but only while the body is still an untouched starter (never clobber
  // edits the admin has made).
  function handleTypeChange(nextType: TemplateType) {
    if (template) return;
    const current = (form.getFieldValue('bodyHtml') as string) ?? '';
    const isUntouched = Object.values(starters).some((s) => s === current) || current.trim() === '';
    if (isUntouched) form.setFieldsValue({ bodyHtml: starters[nextType] });
  }

  // Live preview: re-render through the backend ~700ms after typing stops.
  useEffect(() => {
    if (!open || !body || body.length < 5) return;
    const timer = setTimeout(() => {
      preview
        .mutateAsync({ bodyHtml: body, type })
        .then((r) => { setPreviewHtml(r.html); setPlaceholders(r.placeholders); })
        .catch(() => { /* keep last good preview */ });
    }, 700);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [body, open, type]);

  async function handleSave() {
    try {
      const v = await form.validateFields();
      const input = { code: v.code, name: v.name, type: v.type, language: v.language, bodyHtml: v.bodyHtml, active: v.active };
      if (template) {
        await update.mutateAsync({ id: template.id, input });
        void message.success(t('docTemplates.updated'));
      } else {
        await create.mutateAsync(input);
        void message.success(t('docTemplates.created'));
      }
      onClose();
      form.resetFields();
    } catch (err) {
      if ((err as { errorFields?: unknown[] }).errorFields) return;
      void message.error(asApiError(err).message);
    }
  }

  return (
    <Modal
      title={template ? t('docTemplates.editTitle') : t('docTemplates.newTitle')}
      open={open}
      onCancel={onClose}
      onOk={handleSave}
      confirmLoading={create.isPending || update.isPending}
      width="95%"
      style={{ top: 16, maxWidth: 1500 }}
      styles={{ body: { paddingTop: 12 } }}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        {/* Meta fields */}
        <Row gutter={12} align="bottom">
          <Col xs={24} md={7}>
            <Form.Item name="name" label={t('docTemplates.name')} rules={[{ required: true }]} style={{ marginBottom: 12 }}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item name="code" label={t('docTemplates.code')} rules={[{ required: true }]} style={{ marginBottom: 12 }}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={8} md={4}>
            <Form.Item name="type" label={t('docTemplates.type')} rules={[{ required: true }]} style={{ marginBottom: 12 }}>
              <Select
                onChange={(v) => handleTypeChange(v as TemplateType)}
                options={TYPES.map((tp) => ({ value: tp, label: t(`docTemplates.types.${tp}`) }))}
              />
            </Form.Item>
          </Col>
          <Col xs={8} md={3}>
            <Form.Item name="language" label={t('docTemplates.language')} rules={[{ required: true }]} style={{ marginBottom: 12 }}>
              <Select options={LANGS.map((l) => ({ value: l, label: l.toUpperCase() }))} />
            </Form.Item>
          </Col>
          <Col xs={8} md={4}>
            <Form.Item name="active" label={t('docTemplates.active')} valuePropName="checked" style={{ marginBottom: 12 }}>
              <Switch checkedChildren="●" unCheckedChildren="—" />
            </Form.Item>
          </Col>
        </Row>

        {/* Editor + live preview */}
        <Row gutter={16}>
          <Col xs={24} lg={13}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <Typography.Text strong style={{ fontSize: 13 }}>{t('docTemplates.bodyHtml')}</Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>{t('docTemplates.bodyHint')}</Typography.Text>
            </div>
            <Form.Item name="bodyHtml" rules={[{ required: true, min: 10 }]} style={{ marginBottom: 8 }}>
              <HtmlEditor ref={editorRef} height={540} />
            </Form.Item>
            <PlaceholderReference
              type={type}
              placeholders={placeholders}
              onInsert={(p) => editorRef.current?.insert(`{{${p}}}`)}
            />
          </Col>
          <Col xs={24} lg={11}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <Typography.Text strong style={{ fontSize: 13 }}>
                <EyeOutlined /> {t('docTemplates.previewTitle')}
                {preview.isPending && <Typography.Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>…</Typography.Text>}
              </Typography.Text>
              <Tooltip title={t('docTemplates.previewHint')}>
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  loading={preview.isPending}
                  onClick={() => {
                    const b = form.getFieldValue('bodyHtml') as string;
                    if (b && b.length >= 5) void preview.mutateAsync({ bodyHtml: b, type }).then((r) => { setPreviewHtml(r.html); setPlaceholders(r.placeholders); });
                  }}
                />
              </Tooltip>
            </div>
            {/* A4-like page frame — rendered in an isolated iframe so it matches
                the generated PDF exactly (same wrapper CSS, fonts, table borders). */}
            <div
              style={{
                border: '1px solid #e2e8f0',
                borderRadius: 6,
                height: 540,
                overflow: 'hidden',
                background: '#fff',
                boxShadow: 'inset 0 0 0 1px #f1f5f9',
              }}
            >
              <iframe
                title={t('docTemplates.previewTitle')}
                sandbox=""
                srcDoc={
                  previewHtml ||
                  `<!doctype html><body style="font-family:sans-serif;color:#94a3b8;padding:24px">${t('docTemplates.previewEmpty')}</body>`
                }
                style={{ width: '100%', height: '100%', border: 0, background: '#fff' }}
              />
            </div>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

function PlaceholderReference({ type, placeholders, onInsert }: { type: TemplateType; placeholders: string[]; onInsert?: (p: string) => void }) {
  const { t } = useTranslation();
  const known = useMemo(
    () =>
      type === 'receipt'
        ? [
            'payment.amountFormatted',
            'payment.paymentDate',
            'payment.method',
            'payment.reference',
            'payment.idShort',
            'customer.lastName',
            'customer.firstName',
            'customer.middleName',
            'customer.phone',
            'customer.pinfl',
            'contract.contractNumber',
            'allocations (array; use {{#each allocations}}…{{/each}} with {{this.installmentNumber}}, {{this.dueDate}}, {{this.amountFormatted}})',
          ]
        : [
            'contract.contractNumber',
            'contract.activatedAt',
            'contract.signedDate',
            'contract.firstDueDate',
            'contract.totalAmountFormatted',
            'contract.downPaymentFormatted',
            'contract.remainingFormatted',
            'contract.pricePerSqmFormatted',
            'contract.installmentCount',
            'contract.frequency',
            'customer.lastName',
            'customer.firstName',
            'customer.middleName',
            'customer.phone',
            'customer.email',
            'customer.address',
            'customer.registrationAddress',
            'customer.dateOfBirth',
            'customer.passportSeries',
            'customer.passportNumber',
            'customer.passportIssueDate',
            'customer.passportExpiryDate',
            'customer.passportIssuedBy',
            'customer.pinfl',
            'apartment.fullCode',
            'apartment.number',
            'apartment.areaSqm',
            'apartment.basePrice',
            'apartment.layoutType',
            'schedules (array; use {{#each schedules}}…{{/each}} with {{this.installmentNumber}}, {{this.dueDate}}, {{this.amountDueFormatted}}, {{this.isDownPayment}})',
          ],
    [type],
  );
  return (
    <div
      style={{
        marginTop: 8,
        padding: 10,
        background: '#f8fafc',
        borderRadius: 6,
        fontSize: 12,
        maxHeight: 200,
        overflowY: 'auto',
      }}
    >
      <Typography.Text type="secondary" style={{ fontSize: 11 }}>
        {t('docTemplates.placeholdersAvailable')} — {t('docTemplates.clickToInsert')}
      </Typography.Text>
      <div style={{ marginTop: 4 }}>
        {known.map((p) => {
          const key = p.split(' ')[0]!;
          const insertable = !p.includes(' ');
          return (
            <Tag
              key={p}
              color={placeholders.includes(key) ? 'green' : 'default'}
              style={{ marginBottom: 4, fontSize: 11, cursor: insertable && onInsert ? 'pointer' : 'default' }}
              onClick={insertable && onInsert ? () => onInsert(key) : undefined}
            >
              {`{{${key}}}`}
            </Tag>
          );
        })}
      </div>
      <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 6 }}>
        {t('docTemplates.controlSyntax')}
      </Typography.Text>
    </div>
  );
}
